import { MotionConfig } from 'framer-motion';
import * as React from 'react';
import { REDUCE_MOTION } from '@citylytics/domain/user-settings/constants';
import type { ReduceMotion } from '@citylytics/domain/user-settings/types';
import { ReducedMotionContext } from '@citylytics/ui-web/hooks/use-is-motion-reduced';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
// index.css stops every CSS transition and animation under this attribute, so components need no
// motion-reduce classes of their own.
const REDUCED_MOTION_ATTRIBUTE = 'data-reduced-motion';

function subscribeToSystemPreference(onChange: () => void) {
  const mediaQuery = window.matchMedia(REDUCED_MOTION_QUERY);

  mediaQuery.addEventListener('change', onChange);

  return () => mediaQuery.removeEventListener('change', onChange);
}

function readSystemPreference() {
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

/** Whether motion is reduced now: the saved choice, or the OS preference (live) for `system`. */
function useResolvedReducedMotion(preference: ReduceMotion) {
  const isDeviceReduced = React.useSyncExternalStore(
    subscribeToSystemPreference,
    readSystemPreference,
  );

  return { isReduced: resolveReducedMotion({ preference, isDeviceReduced }), isDeviceReduced };
}

function resolveReducedMotion(params: { preference: ReduceMotion; isDeviceReduced: boolean }) {
  const { preference, isDeviceReduced } = params;

  switch (preference) {
    case REDUCE_MOTION.SYSTEM:
      return isDeviceReduced;
    case REDUCE_MOTION.ON:
      return true;
    case REDUCE_MOTION.OFF:
      return false;
  }
}

export type ReducedMotionProviderProps = {
  /** The user's saved choice; `system` (the default for anyone signed out) follows the OS. */
  preference: ReduceMotion;
  children: React.ReactNode;
};

/**
 * Switches every animation in the app on or off from one place: CSS transitions and animations
 * through the attribute on `<html>`, Framer Motion through `MotionConfig`, and anything else
 * (a map's camera, a video) through `useIsMotionReduced`.
 */
export function ReducedMotionProvider(props: ReducedMotionProviderProps) {
  const { preference, children } = props;
  const { isReduced, isDeviceReduced } = useResolvedReducedMotion(preference);
  const state = React.useMemo(() => ({ isReduced, isDeviceReduced }), [isReduced, isDeviceReduced]);

  React.useLayoutEffect(() => {
    const root = document.documentElement;

    root.setAttribute(REDUCED_MOTION_ATTRIBUTE, String(isReduced));

    return () => root.removeAttribute(REDUCED_MOTION_ATTRIBUTE);
  }, [isReduced]);

  return (
    <ReducedMotionContext.Provider value={state}>
      {/* `reducedMotion` alone still fades opacity; skipping sets every value at once, as the
          CSS rule does, so toasts and tab panels change instantly. */}
      <MotionConfig reducedMotion={isReduced ? 'always' : 'never'} skipAnimations={isReduced}>
        {children}
      </MotionConfig>
    </ReducedMotionContext.Provider>
  );
}
